import Spaces from '@ably/spaces';
import { SpacesProvider } from '@ably/spaces/react';
import * as Ably from 'ably';
import { AblyProvider } from 'ably/react';
import { useMemo } from 'react';

import { NotificationProvider } from '@/contexts/notificationContext';
import useAddMessage from '@/hooks/useAddMessage';
import type { StaffUserSession } from '@/types/staffSession';
import { UserPermissions } from '@/types/user';
import ajax from '@/utils/ajax';

import NotificationChannel from './channels/NotificationsChannel';
import { Color } from './constants/color';
import HOSTS from './constants/hosts';
import { useSidebarLayout } from './contexts/sidebarLayoutContext';
import DugoutRouter from './DugoutRouter';
import Badge from './ui/components/Badge';
import {
  BILLING_SUPER_ADMIN_GROUP,
  BILLING_VIEW_BILLING,
  ENGINEERING_GROUP,
  PEOPLE_VIEW_AI_PROFILE,
  PEOPLE_VIEW_ENCOUNTERS,
  PEOPLE_VIEW_TIMELINE,
  hasPermission,
  isInGroup,
  PEOPLE_VIEW_REWARD_CARDS,
  PEOPLE_IMPERSONATE,
  USER_RESET_MFA,
  PEOPLE_AVAILABILITY_VIEW,
  VOB_EDITORS_GROUP
} from './utils/userPermission';
interface DugoutAuthedAppProps {
  host: string;
  currentUser: StaffUserSession;
  addMessage: ReturnType<typeof useAddMessage>;
  permissions: UserPermissions | undefined;
}

const envBadgeColor = {
  local: Color.BLUE,
  staging: Color.YELLOW,
  production: Color.RED
};

/**
 * Main application component for authenticated users.
 *
 * All config / permissions / current user data is fetched in the parent component (DugoutApp)
 */
function DugoutAuthedApp({ host, currentUser, permissions }: DugoutAuthedAppProps) {
  const { contentOffsetClass } = useSidebarLayout();

  const env = useMemo(
    () => Object.entries(HOSTS).find(([, value]) => value === host)?.[0] || 'local',
    [host]
  );

  const enableTimeline = useMemo((): boolean => {
    const requiredPermission = PEOPLE_VIEW_TIMELINE;

    return !!hasPermission({ permissions, requiredPermission });
  }, [permissions]);

  const enableAiProfile = useMemo((): boolean => {
    const requiredPermission = PEOPLE_VIEW_AI_PROFILE;

    return !!hasPermission({ permissions, requiredPermission });
  }, [permissions]);

  const enablePersonAvailability = useMemo((): boolean => {
    const requiredPermissions = [PEOPLE_AVAILABILITY_VIEW];
    return hasPermission({ permissions, requiredPermissions });
  }, [permissions]);

  const enableBilling = useMemo((): boolean => {
    const requiredPermission = BILLING_VIEW_BILLING;

    return !!hasPermission({ permissions, requiredPermission });
  }, [permissions]);

  const enableEncounters = useMemo((): boolean => {
    const requiredPermission = PEOPLE_VIEW_ENCOUNTERS;
    return hasPermission({ permissions, requiredPermission });
  }, [permissions]);

  const enableRewardCards = useMemo((): boolean => {
    const requiredPermission = PEOPLE_VIEW_REWARD_CARDS;
    return hasPermission({ permissions, requiredPermission });
  }, [permissions]);

  // Engineering only, on top of the permission the API checks.
  const enableImpersonate = useMemo(
    (): boolean =>
      isInGroup(permissions, ENGINEERING_GROUP) &&
      hasPermission({ permissions, requiredPermission: PEOPLE_IMPERSONATE }),
    [permissions]
  );

  const enableMfaReset = useMemo(
    (): boolean => hasPermission({ permissions, requiredPermission: USER_RESET_MFA }),
    [permissions]
  );

  const enableVobList = useMemo(() => isInGroup(permissions, VOB_EDITORS_GROUP), [permissions]);

  const enableBillingTimeline = useMemo(
    () => isInGroup(permissions, BILLING_SUPER_ADMIN_GROUP),
    [permissions]
  );

  const enableEng = isInGroup(permissions, ENGINEERING_GROUP);

  const ablyClient = useMemo(
    () =>
      new Ably.Realtime({
        authCallback: async (tokenParams, callback) => {
          try {
            const response = await ajax.get({
              url: `${host}/api/ably-auth/`,
              params: tokenParams as Record<string, string>
            });
            callback(null, response.data as Ably.TokenRequest);
          } catch (error) {
            callback(error as Ably.ErrorInfo, null);
          }
        }
      }),
    [host]
  );

  const ablySpacesClient = useMemo(() => new Spaces(ablyClient), [ablyClient]);

  const showEnv = env !== 'production' || permissions?.is_super_user;

  return (
    <AblyProvider client={ablyClient}>
      <NotificationChannel user={currentUser}>
        <NotificationProvider />
      </NotificationChannel>
      <SpacesProvider client={ablySpacesClient}>
        <article className="grow bg-gray-100">
          <DugoutRouter
            enableAiProfile={enableAiProfile}
            enableBilling={enableBilling}
            enableEncounters={enableEncounters}
            enableRewardCards={enableRewardCards}
            enableImpersonate={enableImpersonate}
            enableMfaReset={enableMfaReset}
            enableTimeline={enableTimeline}
            enablePersonAvailability={enablePersonAvailability}
            enableVobList={enableVobList}
            enableBillingTimeline={enableBillingTimeline}
            enableEng={enableEng}
          />
        </article>

        <footer
          className={`bg-gray-100 text-gray-600 text-xs p-3 flex justify-center gap-4 ${contentOffsetClass}`}>
          <p className="text">
            <a
              className="text-blue-500 mr-2"
              href={`https://github.com/affect-therapeutics/pyhomebase/commit/${process.env.VITE_APP_GIT_SHA}`}
              target="_blank"
              rel="noreferrer">
              {process.env.VITE_APP_GIT_SHA ? process.env.VITE_APP_GIT_SHA.slice(0, 7) : 'dev'}
            </a>
            {showEnv && (
              <>
                <Badge color={envBadgeColor[env as keyof typeof envBadgeColor]}>{env}</Badge>

                <span className="text-gray-500 px-2">{window.location.hostname}</span>
              </>
            )}
          </p>
        </footer>
      </SpacesProvider>
    </AblyProvider>
  );
}

export default DugoutAuthedApp;
